import axios, { AxiosPromise } from "axios";

import bootbox from "bootbox";

export namespace ModelApi {

    let baseUrl = null;
    let sandboxToken = null;
    let viewToken = null;

    axios.interceptors.response.use(response => {
        return response;
    }, error => {
            const { status } = error.response;
            switch (status) {
                case 403:
                    bootbox.alert("The operation was not permitted");
                    break;
                case 500:
                    bootbox.alert("An error occurred.");
                    break;
            }
            return Promise.reject(error);
    });

    axios.interceptors.request.use(config => {
        if (baseUrl != null) {
            config.baseURL = baseUrl;
        }
        if (sandboxToken != null) {
            config.headers["sandbox"] = sandboxToken;
        }
        if (viewToken != null) {
            config.headers["sd-view-token"] = viewToken;
        }

        return config;
    }, error => Promise.reject(error));

    export function setBaseUrl(url: string) {
        if (url != null) {
            baseUrl = url;
        }
    }

    export function setSandboxToken(token: string) {
        if (token != null) {
            sandboxToken = token;
        }
    }

    export function setViewToken(token: string) {
        if (token != null) {
            viewToken = token;
        }
    }

    export function hasRightsToEditModel(id: string) {
        return axios.get(`/sd/model/edit/${id}`);
    }

    export function getModel(guid: string) {
        return axios.get(`/sd/model/${guid}${location.search}`);
    }

    export function getComboModel(guid: string, subModelId: string = null) {
        var search = new URLSearchParams(location.search);
        if (subModelId != null)
            search.append("primarySubModelId", subModelId);

        return axios.get(`/sd/combomodel/${guid}${search.toString().length > 0 ? "?" + search.toString() : ""}`);
    }

    export function getNodeColors(urn: string, schemeId: string): AxiosPromise<any> {
        return axios.get(`/sd/api/modelColor/colors/${urn}/${schemeId}`);
    }

    export function getColorSchemes(urn: string): AxiosPromise<any> {
        return axios.get(`/sd/api/modelColor/schemes/${urn}`);
    }

    export function getPropertyRules(urn: string, role: string): AxiosPromise<any> {
        return axios.get(`/sd/api/propertyData/rules/${urn}/${role}`);
    }

    export function getIdMap(id: string): AxiosPromise<any> {
        return axios.get(`/sd/${id}/idmap.json`);
    }

    export function getExternalIdMap(id: string): AxiosPromise<any> {
        return axios.get(`/sd/${id}/externalidmap.json`);
    }

    export function getPdmsAttr(id: string): AxiosPromise<any> {
        return axios.get(`/sd/${id}/pdmsattr.json`);
    }

    export function getModelFile(id: string, filename: string): AxiosPromise<any> {
        return axios.get(`/sd/${id}/${filename}`);
    }

    export function getExternalData(id: string, filename: string): AxiosPromise<any> {
        return axios.get(`/sd/externalData/${id}/${filename}`);
    }

    export function updateExternalData(id: string, data: any) {
        return axios.put(`/sd/api/extData/updateExternalData/${id}`, data);
    }

    export function getTourData(id: string, filename: string): AxiosPromise<any> {
        return axios.get(`/sd/tourData/${id}/${filename}`);
    }

    export function getJsonData(id: string, filename: string): AxiosPromise<any> {
        return axios.get(`/sd/jsonData/${id}/${filename}${location.search}`);
    }

    export function saveJsonData(id: string, filename: string, data: any) {
        return axios.put(`/sd/saveJsonData/${id}/${filename}${location.search}`, data);
    }

    export function getMeasurements(urn: string): AxiosPromise<any> {
        return axios.get(`/sd/api/measurement/measurements/${urn}`);
    }

    export function getModelRoles(urn: string): AxiosPromise<any> {
        return axios.get(`/sd/api/modelRole/roles/${urn}`);
    }

    export function getComboModelRoles(id: string): AxiosPromise<any> {
        return axios.get(`/sd/api/modelRole/comboRoles/${id}`);
    }

    export function getModelRoleTools(role: string): AxiosPromise<any> {
        return axios.get(`/sd/api/modelRole/roleTools/${role}`);
    }

    export function getModelTree(id: string): AxiosPromise<any> {
        return axios.get(`/sd/api/modelStructure/modelTree/${id}`);
    }

    export function getCountryReport(id: string, url: string, period: string): AxiosPromise<any> {
        return axios.get(`/sd/api/report/countries/${id}/${url}/${period}`);
    }

    export function getUniqueVisitorCountryReport(id: string, url: string, period: string): AxiosPromise<any> {
        return axios.get(`/sd/api/report/uniqueVisitorCountries/${id}/${url}/${period}`);
    }

    export function getPageViewReport(id: string, url: string, period: string): AxiosPromise<any> {
        return axios.get(`/sd/api/report/pageViews/${id}/${url}/${period}`);
    }

    export function getUniquePageVisitorReport(id: string, url: string, period: string): AxiosPromise<any> {
        return axios.get(`/sd/api/report/uniquePageVisitors/${id}/${url}/${period}`);
    }

    export function getAllTimePageViewReport(urls: string[]): AxiosPromise<any> {
        return axios.get(`/sd/api/report/allTimePageViews?ids=${urls.join()}`);
    }

    export function getAllTimeUniquePageVisitorReport(urls: string[]): AxiosPromise<any> {
        return axios.get(`/sd/api/report/allTimeUniquePageVisitors?ids=${urls.join()}`);
    }

    export function commitUploadedModels(metadata): AxiosPromise<any> {
        return axios.post("/sd/api/upload/commitFiles", metadata);
    }

    export function checkUploadStatus(queryIds): AxiosPromise<any> {
        return axios.post("/sd/api/upload/checkStatus", queryIds);
    }

    export function revertUploadedModels(serverIds): AxiosPromise<any> {
        return axios.delete("/sd/api/upload/revert", {data: serverIds});
    }

    export function getMeasurementDataRanges(): AxiosPromise<any> {
        return axios.get("/sd/api/Status/ranges");
    }

    export function getMeasurementData(id, source, refresh: boolean): AxiosPromise<any> {
        return axios.get(`/sd/api/Status/${source}?id=${id}&refresh=${refresh}`);
    }

    export function createShare(shareData): AxiosPromise<any> {
        return axios.post("/sd/api/share/createShare", shareData);
    }

    export function getModels(group, projectId): AxiosPromise<any> {
        return axios.get(`/sd/api/project/models/${group}/${projectId}`);
    }

    export function createComboModel(comboModelData): AxiosPromise<any> {
        return axios.post("/sd/api/project/createComboModel", comboModelData);
    }

    export function getComboModels(group, projectId): AxiosPromise<any> {
        return axios.get(`/sd/api/project/comboModels/${group}/${projectId}`);
    }

    export function deleteModel(group, projectId, modelId): AxiosPromise<any> {
        return axios.delete(`/sd/api/project/deleteModel/${group}/${projectId}/${modelId}`);
    }

    export function deleteComboModel(group, projectId, modelId): AxiosPromise<any> {
        return axios.delete(`/sd/api/project/deleteComboModel/${group}/${projectId}/${modelId}`);
    }

    export function getComboModelBcfData(id: string, filename: string): AxiosPromise<any> {
        return axios.get(`/sd/combomodel/bcf/${id}/${filename}${location.search}`);
    }
}